/**
 * Phone editor for one logged application: the amount on the in-app keypad, the date, the
 * "why" chips and a note. The product is shown, not changed (a wrong product is a delete and
 * re-log, which supervisors do). Save sends only what changed, with the last_modified this
 * screen loaded, so someone else's edit is never overwritten silently.
 */
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronLeft } from 'lucide-react';
import { ApiError, saveFailureMessage } from '@/lib/apiErrors';
import { cn } from '@/lib/cn';
import { todayInPH } from '@/lib/phDate';
import { Keypad } from '@/components/entry/Keypad';
import { amountText, categoryLabel } from '@/components/pond/page/format';
import { useReadingReasons } from '@/components/pond/page/data';
import { LOCK_MESSAGE, useApplicationActions, type ApplicationChanges } from '@/components/pond/page/useApplicationActions';
import { bigAmountLimit, fitsPrecision, PRECISION_HINT } from '@/lib/applicationAmount';
import type { ApplicationResponse } from '@/types/generated/openapi';

const ROUTINE = 'Routine';
const OTHER = 'other';

interface Props {
  farmId: string;
  cropId: string;
  row: ApplicationResponse;
  canDelete: boolean;
  onClose: () => void;
}

export function PhoneApplicationEdit({ farmId, cropId, row, canDelete, onClose }: Props) {
  const { update, remove } = useApplicationActions(farmId);
  const readingReasons = useReadingReasons(farmId, cropId, row.application_date);
  const [amount, setAmount] = useState(String(Number(row.amount ?? 0)));
  const [date, setDate] = useState(row.application_date);
  const [reasonPick, setReasonPick] = useState(!row.reason ? '' : row.reason === ROUTINE ? ROUTINE : OTHER);
  const [reasonText, setReasonText] = useState(row.reason && row.reason !== ROUTINE ? row.reason : '');
  const [notes, setNotes] = useState(row.notes ?? '');
  const [hint, setHint] = useState('');
  const [textFocus, setTextFocus] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [bigOk, setBigOk] = useState<number | null>(null);
  // Typing starts from the keypad's first digit, not from the loaded value appended to.
  const [freshAmount, setFreshAmount] = useState(true);
  const amountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    amountRef.current?.scrollIntoView({ block: 'nearest' });
  }, [amount]);

  const amountNum = amount === '' || amount === '.' ? NaN : Number(amount);
  const reason = reasonPick === OTHER ? reasonText.trim() : reasonPick;
  const changes: ApplicationChanges = {};
  if (Number.isFinite(amountNum) && amountNum !== Number(row.amount)) changes.amount = amountNum;
  if (date !== row.application_date) changes.application_date = date;
  if (reason !== (row.reason ?? '')) changes.reason = reason || null;
  if (notes.trim() !== (row.notes ?? '')) changes.notes = notes.trim() || null;
  const dirty = Object.keys(changes).length > 0;
  const busy = update.isPending || remove.isPending;
  const error = update.error ?? remove.error;
  const conflict = error instanceof ApiError && error.status === 409 && LOCK_MESSAGE.test(error.message);

  const submit = () => {
    if (busy) return;
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setHint('Type the amount');
      return;
    }
    if (!dirty) {
      onClose();
      return;
    }
    if (changes.amount !== undefined && amountNum > bigAmountLimit(Number(row.amount) || 0, row.unit ?? '') && bigOk !== amountNum) {
      setBigOk(amountNum);
      setHint(`${amountText(amountNum, row.unit)} is a lot. Tap Save again if it is right.`);
      return;
    }
    setHint('');
    update.mutate(
      { row, changes },
      {
        onSuccess: () => {
          toast.success(`Saved ${row.product_name} ${amountText(amountNum, row.unit)}`);
          onClose();
        },
      }
    );
  };

  const onDigit = (c: string) => {
    if (busy) return;
    const base = freshAmount ? '' : amount;
    let next: string;
    if (c === '.') {
      if (base.includes('.')) return;
      next = `${base || '0'}.`;
    } else {
      next = base === '0' ? c : base + c;
    }
    if (!fitsPrecision(next)) {
      setHint(PRECISION_HINT);
      return;
    }
    setFreshAmount(false);
    setHint('');
    setBigOk(null);
    setAmount(next);
  };
  const onHalf = () => {
    if (busy) return;
    setFreshAmount(false);
    setHint('');
    setAmount(`${(freshAmount ? '' : amount).split('.')[0] || '0'}.5`);
  };

  const dayText = row.application_date === todayInPH() ? 'Today' : row.application_date;
  const showKeypad = !textFocus && !confirmDelete;
  const unit = row.unit ?? '';

  return (
    <div className="fixed inset-0 z-[55] flex flex-col bg-gray-100" data-phone-edit role="dialog" aria-modal="true" aria-label="Edit application">
      <header className="flex shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-4 pb-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          aria-label="Cancel"
          className="-ml-2 flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-900 active:bg-gray-100"
        >
          <ChevronLeft className="h-6 w-6" aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[17px] font-bold text-gray-900">{row.product_name || 'Application'}</h1>
          <p className="text-xs text-gray-500">
            {categoryLabel(row.product_category)} · {dayText}
          </p>
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-3">
        {confirmDelete ? (
          <div className="space-y-3 rounded-2xl border border-red-200 bg-red-50 p-4" data-delete-confirm>
            <p className="text-[15px] font-semibold text-red-900">
              Delete {row.product_name} {amountText(row.amount, row.unit)}?
            </p>
            <p className="text-sm text-red-800">It leaves the journal and the totals. The change is recorded.</p>
            {remove.isError && (
              <p role="alert" className="text-sm font-semibold text-red-900">
                Not deleted: {saveFailureMessage(remove.error, 'try again')}
              </p>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                remove.mutate(row, {
                  onSuccess: () => {
                    toast.success('Deleted');
                    onClose();
                  },
                })
              }
              className="flex min-h-12 w-full items-center justify-center rounded-xl bg-red-600 text-[15px] font-semibold text-white disabled:opacity-50"
            >
              {remove.isPending ? 'Deleting…' : 'Yes, delete it'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirmDelete(false)}
              className="flex min-h-12 w-full items-center justify-center rounded-xl border border-gray-300 bg-white text-[15px] font-semibold text-gray-900"
            >
              Keep it
            </button>
          </div>
        ) : (
          <>
            <div
              ref={amountRef}
              className="scroll-my-3 flex items-center justify-between rounded-xl border-2 border-primary-600 bg-white px-3.5 py-3"
              data-amount
            >
              <span className="text-[15px] text-gray-900">Amount</span>
              <span className="flex items-baseline text-[28px] font-bold tabular-nums text-gray-900" data-amount-value>
                {amount === '' ? <span className="text-gray-400">—</span> : amount.startsWith('.') ? `0${amount}` : amount}
                {unit && <span className="ml-1 text-base font-medium text-gray-500">{unit}</span>}
                <span className="ml-0.5 inline-block h-6 w-0.5 animate-pulse self-center bg-primary-600" aria-hidden="true" />
              </span>
            </div>

            <label className="flex flex-col gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Date
              <input
                type="date"
                value={date}
                max={todayInPH()}
                onChange={(e) => e.target.value && setDate(e.target.value)}
                onFocus={() => setTextFocus(true)}
                onBlur={() => setTextFocus(false)}
                className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 text-base font-normal normal-case tracking-normal text-gray-900"
              />
            </label>

            <fieldset className="space-y-1.5">
              <legend className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Why this application · optional</legend>
              <div className="flex flex-wrap gap-2">
                {[...readingReasons, ROUTINE].map((r) => (
                  <Chip key={r} label={r} chosen={reasonPick === r} onPick={() => setReasonPick((c) => (c === r ? '' : r))} />
                ))}
                <Chip label="Other" chosen={reasonPick === OTHER} onPick={() => setReasonPick((c) => (c === OTHER ? '' : OTHER))} />
              </div>
              {reasonPick === OTHER && (
                <input
                  type="text"
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  onFocus={() => setTextFocus(true)}
                  onBlur={() => setTextFocus(false)}
                  placeholder="Why this application?"
                  aria-label="Other reason"
                  className="mt-2 min-h-11 w-full rounded-lg border border-gray-300 px-3 text-base"
                />
              )}
            </fieldset>

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onFocus={() => setTextFocus(true)}
              onBlur={() => setTextFocus(false)}
              rows={2}
              placeholder="Note (optional)"
              aria-label="Note"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base"
            />

            {hint && (
              <p className="px-1 text-sm font-medium text-amber-800" role="alert">
                {hint}
              </p>
            )}
            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-800" role="alert" data-edit-error>
                {conflict ? 'Changed by someone else just now. Go back and open it again.' : `Not saved: ${saveFailureMessage(error, 'try again')}`}
              </p>
            )}

            {canDelete && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                disabled={busy}
                className="flex min-h-11 items-center px-1 text-[13px] font-semibold text-red-700"
              >
                Delete this entry…
              </button>
            )}
          </>
        )}
      </div>

      {showKeypad && (
        <Keypad
          className="shrink-0"
          onDigit={onDigit}
          onDelete={() => {
            setFreshAmount(false);
            setAmount(amount.slice(0, -1));
            setBigOk(null);
          }}
          onClear={() => {
            setFreshAmount(false);
            setAmount('');
          }}
          digitsDisabled={busy}
          secondaryKey={{ label: '½', ariaLabel: 'Half (set to point 5)', onPress: onHalf }}
          actionKey={{ label: update.isPending ? 'Saving…' : 'Save', onPress: submit, disabled: busy }}
          announcement={`${hint ? `${hint}. ` : ''}Amount: ${amount === '' ? 'blank' : `${amount} ${unit}`}`}
        />
      )}

      {textFocus && !confirmDelete && (
        <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-2">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => (document.activeElement as HTMLElement | null)?.blur()}
            className="flex min-h-12 w-full items-center justify-center rounded-xl bg-primary-600 text-[15px] font-semibold text-white"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}

function Chip({ label, chosen, onPick }: { label: string; chosen: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={chosen}
      onClick={onPick}
      className={cn(
        'min-h-11 min-w-11 max-w-full rounded-full border px-4 text-sm font-medium touch-manipulation',
        chosen ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white text-gray-700'
      )}
    >
      {label}
    </button>
  );
}
